/* eslint-disable no-nested-ternary */
import React from 'react';
import { FormattedHTMLMessage, injectIntl } from 'react-intl';
import { produce } from 'immer';
import {
    VerificationService,
    StudentPersonalInfoViewModel,
    CountDownComponent,
    getLogoUrl,
    getMaxReviewTime,
    logger,
    LogoComponent,
    PoweredByComponent,
    SSOPendingComponent,
    usePollingInterval,
    VerificationStepsEnum,
    FormFooterComponent,
    ReviewPendingComponent,
} from '@sheerid/jslib';
import { getPreviousStepOverride } from '../../lib/ServerApi/TestingRouteOverrides';
import { PENDING_REDIRECT } from '../../constants';
import { redirect } from '../../lib/utils/browser';

const DOC_UPLOAD_POLL_DURATION_DURING_COUNTDOWN = 5000;
const DOC_UPLOAD_POLL_DURATION = 20000;
const SSO_POLL_DURATION = 5000;

interface StepPendingProps {
    verificationService: VerificationService;
    intl: any;
}

const StepPending = ({ verificationService, intl }: StepPendingProps) => {
    const { programId } = verificationService;
    const { viewModel } = verificationService;
    const previousViewModel = (verificationService.previousViewModel as StudentPersonalInfoViewModel) || undefined;
    const verificationLastResponse = verificationService.verificationResponse.lastResponse || verificationService.previousVerificationResponse || undefined;
    const { verificationId } = verificationService.verificationResponse;
    const hasLogo = !!getLogoUrl(verificationService.programTheme);
    const countdownSeconds = 20;
    const redirectDestination = `${PENDING_REDIRECT}${verificationId}`;

    const isPreviousStepSSO = (): boolean => {
        if (verificationLastResponse && verificationLastResponse.currentStep === VerificationStepsEnum.sso) {
            return true;
        }
        return false;
    };

    const [showCountDown, setShowCountDown] = React.useState(!isPreviousStepSSO());
    const getLogo = () => (
        hasLogo ? (
            <LogoComponent verificationService={verificationService} />
        ) : (
            <img
                className="sid-l-lead-image"
                alt="design element"
                src="https://assets-resources.sheerid.com/common/images/2018/icons/doc-pending.svg"
            />
        )
    );

    const pollTime = isPreviousStepSSO()
        ? SSO_POLL_DURATION
        : showCountDown ? DOC_UPLOAD_POLL_DURATION_DURING_COUNTDOWN : DOC_UPLOAD_POLL_DURATION;

    const stopPollingInterval = usePollingInterval(() => {
        verificationService.fetchExistingVerificationRequest(programId, verificationId, verificationLastResponse, previousViewModel, false);
    }, pollTime);

    const cancelSSOStep = () => {
        const nextState = produce(viewModel, () => {});
        logger.info('StepSSOComponent submitting cancel request');
        stopPollingInterval();

        verificationService.submitStep(VerificationStepsEnum.sso, nextState, verificationLastResponse);
    };

    const onCountdownFinish = () => {
        setShowCountDown(false);
    };

    const logo = getLogo();

    const isMockingPreviousStepSSO = (getPreviousStepOverride() && getPreviousStepOverride() === VerificationStepsEnum.sso);

    return (
        <div id="sid-step-pending" className="sid-l-container">
            { isPreviousStepSSO() || isMockingPreviousStepSSO ? (
                <SSOPendingComponent logo={logo} cancelSSO={cancelSSOStep} />
            ) : showCountDown ? (
                <CountDownComponent logo={logo} onCountdownFinish={onCountdownFinish} countdownSeconds={countdownSeconds} />
            ) : (
                <>
                    <ReviewPendingComponent
                        verificationService={verificationService}
                        logo={logo}
                        intl={intl}
                        maxReviewTime={getMaxReviewTime(verificationService.programTheme)}
                    />
                    <div className="sid-submit">
                        <button
                            id="sid-pending-btn"
                            onClick={() => redirect(redirectDestination)}
                            type="submit"
                            className="sid-btn sid-btn--dark sid-l-full-width"
                        >
                            <span id="sid-pending-btn__text">
                                <FormattedHTMLMessage id="step.pending.redirectText" defaultMessage="Finish" />
                            </span>
                        </button>
                    </div>
                </>
            )}

            { showCountDown ? (
                <PoweredByComponent verificationService={verificationService} />
            ) : (
                <FormFooterComponent verificationService={verificationService}>
                    <div />
                    {' '}
                    {/* suppress default */}
                </FormFooterComponent>
            )}
        </div>
    );
};

export const StepPendingComponent = injectIntl(StepPending);
